import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Oval } from "react-loader-spinner";
import Breadcrumbs from "../../common/Breadcrumbs/Breadcrumbs";
import { loggedInInfluencerChannelList } from "../../../store/influencerCampaignStore/actions/InfluencerCampaignActions/loggedInInfluencerChannelListActions";
import { FaRobot } from "react-icons/fa";
import { MdOutlineBackHand } from "react-icons/md";
import { getUserInfo } from "../../../store/authStore/actions/authActions/getUserInfoActions";

const MyChannels = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { loading, data } = useSelector(
        (state) => state.loggedInInfluencerChannelListReducer
    );

    // Source of truth for access types and token expiry
    const userInfo = useSelector((state) => state.getUserInfoReducer.data);

    console.log("data", data);

    // Determine if both Instagram and YouTube channels are already connected
    const channelList = Array.isArray(data) ? data : [];
    const platformSet = new Set(
        channelList.map((ch) => (ch?.platform || '').toString().toUpperCase())
    );
    const hasBothPlatforms = platformSet.has('YOUTUBE') && platformSet.has('INSTAGRAM');

    // Decide if a given channel item should be treated as Automated (FULL_ACCESS & token not expired)
    const isAutomatedForItem = (item) => {
        const platform = String(item?.platform || '').toUpperCase();
        const toBool = (val) => (val === true || String(val).toLowerCase() === 'true');
        if (platform === 'INSTAGRAM') {
            const access = item?.channelAccessTypeInstagram ?? userInfo?.channelAccessTypeInstagram;
            const tokenExpired = toBool(userInfo?.isInstagramTokenExpire);
            return access === 'FULL_ACCESS' && !tokenExpired;
        }
        // Default to YouTube
        const access = item?.channelAccessType ?? userInfo?.channelAccessType;
        const tokenExpired = toBool(userInfo?.isTokenExpire);
        return access === 'FULL_ACCESS' && !tokenExpired;
    };

    useEffect(() => {
        const handleFocus = () => {
            dispatch(loggedInInfluencerChannelList({}));
            dispatch(getUserInfo());
        };

        window.addEventListener("focus", handleFocus);

        // Initial fetch
        dispatch(loggedInInfluencerChannelList({}));
        dispatch(getUserInfo());

        return () => {
            window.removeEventListener("focus", handleFocus);
        };
    }, [dispatch]);

    /**
     * Render list of channels with graceful handling for loading / empty states
     */
    const renderChannels = () => {
        if (loading) {
            return (
                <div className="flex justify-center items-center w-full h-40">
                    <Oval height={50} width={50} color="#0D9B50" ariaLabel="loading" />
                </div>
            );
        }

        const list = Array.isArray(data) ? data : [];

        if (list.length === 0) {
            return (
                <div className="w-full flex justify-center items-center py-8">
                    <p className="text-[1.6rem] text-gray-500">
                        No channels connected yet.
                    </p>
                </div>
            );
        }

        return list.map((item, idx) => (
            <div
                key={idx}
                className="w-full bg-white shadow-sm border border-[#E5E7EB] rounded-[1.2rem] p-6 flex flex-col md:flex-row md:items-center md:justify-between mb-6"
            >
                {/* Left – Avatar & details */}
                <div className="flex items-start">
                    <div className="w-[9rem] h-[9rem] rounded-full overflow-hidden mr-4">
                        <img
                            src={
                                item?.platform === "INSTAGRAM"
                                    ? (item?.metaData?.profile_picture_url || "https://placehold.co/96x96")
                                    : (item?.metaData?.snippet?.thumbnails?.high?.url || "https://placehold.co/96x96")
                            }
                            alt={item?.metaData?.snippet?.title || "Channel"}
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="flex flex-col">
                        <div className="flex items-center">
                            <h3 className="text-[2rem] inter-font font-semibold text-[#202224] capitalize mr-3">
                                {item.channelName || "--"}
                            </h3>
                            <span
                                className={`mr-4 text-[1.2rem] flex items-center inter-font font-[400] px-3 py-1 rounded-full bg-[#DCFCE7] text-[#166534]`}
                            >
                                {item.platform === "INSTAGRAM" ? item.channelAccessType === "FULL_ACCESS" ?
                                    <FaRobot size={14} color="#0D9B50" className="mr-[0.3rem]" />
                                    :
                                    <MdOutlineBackHand size={14} color="#0D9B50" className="mr-[0.3rem]" />
                                    :
                                    item.channelAccessType === "FULL_ACCESS" ?
                                        <FaRobot size={14} color="#0D9B50" className="mr-[0.3rem]" />
                                        :
                                        <MdOutlineBackHand size={14} color="#0D9B50" className="mr-[0.3rem]" />
                                }
                                {item.platform === "INSTAGRAM" ? item.channelAccessType === "FULL_ACCESS" ? "Automated" : "Manual" : item.channelAccessType === "FULL_ACCESS" ? "Automated" : "Manual"}
                            </span>
                        </div>
                        {item.channelId && (
                            <span className="text-[1.6rem] inter-font font-[500] text-gray-500">
                                @{item.platformChannelId}
                            </span>
                        )}
                        <span className="text-[1.4rem] inter-font font-[400] text-gray-500">
                            Platform - {item.platform || "-"}
                        </span>
                    </div>
                </div>

                {/* Right – Access type & button */}
                <div className="flex items-center mt-4 md:mt-0">
                    <button
                        className="bg-[#0D9B50] hover:bg-[#128e4b] text-white text-[1.4rem] inter-font font-[500] px-6 py-2.5 rounded-xl"
                        onClick={() => window.open(`/influencer/choose-access-type?platform=${encodeURIComponent(item.platform || '')}`, '_blank')}
                    >
                        Change
                    </button>
                </div>
            </div>
        ));
    };

    return (
        <div className="w-full h-full bg-[#EDEEF1] overflow-y-scroll">
            <Breadcrumbs breadcrumbs={[
                { label: "Home", href: "/" },
                { label: "My Channel", href: "/brand/my-channels" },
            ]} />
            <div className="px-[4rem]">
                <div className="flex justify-between items-center">
                    <div>
                        <h2 className="text-[1.8rem] inter-font font-[600] text-[#534D4D] mt-4">
                            My Channel
                        </h2>
                        <p className="text-[1.8rem] inter-font font-[400] text-[#7F8387] mb-8">
                            Select how you'd like to manage your content publishing with PaidPromo
                        </p>
                    </div>
                    {!hasBothPlatforms && (
                        <button
                            className="bg-[#0D9B50] hover:bg-[#128e4b] text-white text-[1.6rem] inter-font font-[500] px-6 py-2.5 rounded-xl"
                            onClick={() => window.open('/influencer-channel', '_blank')}
                        >
                            Add New Channel
                        </button>
                    )}
                </div>

                {renderChannels()}
            </div>
        </div>
    );
};

export default MyChannels;
